import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const QUOTE_URL = '/quote/sports?ref=highstreet';

const HERO_IMG = 'https://images.unsplash.com/photo-1777489689168-497290f235d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxraWRzJTIwcGxheWluZyUyMHNwb3J0cyUyMHBhcmslMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzkxMjE2MTUzfDA&ixlib=rb-4.1.0&q=85&w=2000';
const CARD_IMG_LEAGUE = 'https://images.unsplash.com/photo-1781938891114-a4782a49d988?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHw0fHxraWRzJTIwcGxheWluZyUyMHNwb3J0cyUyMHBhcmslMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzkxMjE2MTUzfDA&ixlib=rb-4.1.0&q=85&w=1200';
const CARD_IMG_EVENT = 'https://images.unsplash.com/photo-1790836481394-6a8f88ae4003?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxraWRzJTIwcGxheWluZyUyMHNwb3J0cyUyMHBhcmslMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzkxMjE2MTUzfDA&ixlib=rb-4.1.0&q=85&w=1200';

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

const QuoteButton = ({ children, variant = 'primary', size = 'lg', testid }) => (
  <Link to={QUOTE_URL} className={`hs-btn hs-btn-${variant} hs-btn-${size}`} data-testid={testid}>
    <span>{children}</span>
    <span className="hs-pip"><span className="hs-arrow"><Arrow /></span></span>
  </Link>
);

const AnchorButton = ({ children, href, variant = 'outline', size = 'lg', testid }) => (
  <a href={href} className={`hs-btn hs-btn-${variant} hs-btn-${size}`} data-testid={testid}>
    <span>{children}</span>
    <span className="hs-pip"><span className="hs-arrow"><Arrow /></span></span>
  </a>
);

const ArrowLink = ({ children, href = QUOTE_URL, testid }) => {
  const inner = (
    <>
      <span>{children}</span>
      <span className="hs-al-ar"><Arrow /></span>
    </>
  );
  if (href.startsWith('/') && !href.includes('#')) {
    return <Link to={href} className="hs-arrowlink" data-testid={testid}>{inner}</Link>;
  }
  return <a href={href} className="hs-arrowlink" data-testid={testid}>{inner}</a>;
};

const COVERAGE = [
  { h: 'General liability', lim: '$1M / $3M' },
  { h: 'Participant accident', lim: '$25K per athlete' },
  { h: 'Equipment & property', lim: 'Scheduled coverage' },
  { h: 'Tournaments & events', lim: 'Short-term coverage' },
];

const SAFETY = [
  { h: 'Safety policies & templates', p: 'Ready-to-adopt libraries — emergency action plans, return-to-play, communication guidelines.' },
  { h: 'Background screening', p: 'Guidance and tools to screen coaches, staff, and volunteers the right way.' },
  { h: 'Abuse prevention', p: 'Education and one-on-one interaction policies that protect every athlete.' },
  { h: 'Athlete Safety Score', p: "A measurable view of your organization's risk — and a track record that strengthens your renewal." },
];

const FAQ = [
  { q: 'Who is eligible for this program?', a: 'Youth and amateur sports organizations — clubs, leagues, camps, tournaments, and training programs. You work with your local Highstreet agency, backed by a sports-specialist underwriting team.' },
  { q: 'How fast can I get a quote?', a: 'The application takes about five minutes. A sports-specialist team reviews your submission and returns a bindable quote, typically within 24 hours — not a ballpark estimate.' },
  { q: 'What does the quote cost?', a: "Nothing. The quote is free and carries no obligation. Your premium depends on your sport, participant count, and coverage selections — you'll see exact numbers before you decide anything." },
  { q: 'Is the athlete safety platform really included?', a: 'Yes. Every policy includes the Players Health athlete safety platform at no added cost — policy templates, screening guidance, and your Athlete Safety Score. It is a core part of the program, not an upsell.' },
  { q: 'What if I already have coverage?', a: "It doesn't hurt to get another quote to see if there are savings — or a stronger safety program behind the same price." },
];

const HS_CSS = `
.hs-page{
  --hs-yellow:#ffdd00; --hs-yellow-600:#f0cf00; --hs-amber:#ffc404; --hs-orange:#fda307;
  --hs-black:#000000; --hs-bone:#faf6f5; --hs-g100:#f1eeed; --hs-g200:#e2dedd;
  --hs-g400:#b4afad; --hs-g600:#6b6664; --hs-g800:#2b2828;
  --hs-font:'Figtree',system-ui,-apple-system,'Segoe UI',sans-serif;
  --hs-ease:cubic-bezier(.22,.61,.36,1); --hs-dur:220ms; --hs-fast:120ms;
  font-family:var(--hs-font); color:var(--hs-black); background:#ffffff;
  -webkit-font-smoothing:antialiased; overflow-x:hidden; scroll-behavior:smooth;
}
.hs-page *{box-sizing:border-box;}
.hs-page img{max-width:100%;display:block;}
.hs-wrap{max-width:1280px;margin:0 auto;padding:0 40px;}
.hs-section{padding:96px 0;}
.hs-eyebrow{font-weight:700;font-size:14px;letter-spacing:.08em;text-transform:uppercase;color:var(--hs-g600);display:inline-block;margin-bottom:18px;}
.hs-display-1{margin:0;font-weight:400;font-size:clamp(44px,7.6vw,100px);line-height:.98;letter-spacing:-.02em;}
.hs-display-2{margin:0;font-weight:400;font-size:clamp(32px,5vw,68px);line-height:1.0;letter-spacing:-.02em;}
.hs-h2{margin:0;font-weight:400;font-size:clamp(28px,3.4vw,44px);line-height:1.08;letter-spacing:-.01em;}
.hs-lead{margin:0;font-size:clamp(17px,1.6vw,22px);line-height:1.5;}
.hs-sec-head{max-width:680px;margin-bottom:48px;}
.hs-sec-head p{margin:16px 0 0;font-size:17px;line-height:1.6;color:var(--hs-g600);}

/* ---------- Buttons ---------- */
.hs-btn{display:inline-flex;align-items:center;border-radius:999px;font-family:var(--hs-font);
  font-weight:700;line-height:1;text-decoration:none;cursor:pointer;border:none;white-space:nowrap;
  transition:filter var(--hs-dur) var(--hs-ease),transform var(--hs-fast) var(--hs-ease);}
.hs-btn:hover{filter:brightness(.94);}
.hs-btn:active{transform:scale(.98);}
.hs-btn-lg{height:64px;padding-left:32px;padding-right:8px;font-size:18px;gap:16px;}
.hs-btn-md{height:54px;padding-left:26px;padding-right:7px;font-size:16px;gap:14px;}
.hs-btn-sm{height:44px;padding-left:20px;padding-right:6px;font-size:15px;gap:12px;}
.hs-btn-primary{background:var(--hs-yellow);color:#000;}
.hs-btn-primary .hs-pip{background:#000;color:var(--hs-yellow);}
.hs-btn-inverse{background:#000;color:#fff;}
.hs-btn-inverse .hs-pip{background:var(--hs-yellow);color:#000;}
.hs-btn-outline{background:transparent;color:#000;border:2px solid #000;}
.hs-btn-outline .hs-pip{background:#000;color:var(--hs-yellow);}
.hs-btn-onphoto{background:transparent;color:#fff;border:2px solid rgba(255,255,255,.75);}
.hs-btn-onphoto .hs-pip{background:#fff;color:#000;}
.hs-pip{border-radius:50%;display:inline-flex;align-items:center;justify-content:center;flex:none;}
.hs-btn-lg .hs-pip{width:48px;height:48px;}
.hs-btn-md .hs-pip{width:40px;height:40px;}
.hs-btn-sm .hs-pip{width:32px;height:32px;}
.hs-arrow{width:46%;height:46%;display:inline-flex;}

.hs-arrowlink{display:inline-flex;align-items:center;gap:10px;font-weight:700;font-size:17px;
  color:#000;text-decoration:none;transition:opacity var(--hs-dur) var(--hs-ease);}
.hs-arrowlink:hover{opacity:.7;}
.hs-al-ar{width:20px;height:20px;display:inline-flex;transition:transform var(--hs-dur) var(--hs-ease);}
.hs-arrowlink:hover .hs-al-ar{transform:translateX(3px);}

/* ---------- Header ---------- */
.hs-header{position:absolute;top:0;left:0;right:0;z-index:20;}
.hs-utility{display:flex;justify-content:flex-end;padding:12px 40px;font-weight:700;font-size:14px;}
.hs-utility a{display:inline-flex;align-items:center;gap:8px;color:#fff;text-decoration:none;opacity:.9;}
.hs-utility a:hover{opacity:1;}
.hs-navbar{display:flex;align-items:center;gap:36px;background:#fff;
  border-radius:0 32px 32px 0;box-shadow:0 8px 24px rgba(0,0,0,.10);
  padding:16px 28px 16px 40px;margin-right:24px;}
.hs-cobrand{display:flex;align-items:center;gap:16px;}
.hs-cobrand img.hs-lg-logo{height:40px;width:auto;}
.hs-cobrand img.hs-ph-logo{height:28px;width:auto;}
.hs-cobrand .x{font-weight:600;font-size:18px;color:var(--hs-g400);}
.hs-nav-links{display:flex;align-items:center;gap:30px;margin-left:12px;}
.hs-nav-links a{font-weight:700;font-size:16px;color:#000;text-decoration:none;padding-bottom:3px;border-bottom:3px solid transparent;transition:border-color var(--hs-dur) var(--hs-ease);}
.hs-nav-links a:hover{border-bottom-color:var(--hs-yellow);}
.hs-nav-cta{margin-left:auto;}

/* ---------- Hero ---------- */
.hs-hero{position:relative;min-height:760px;display:flex;align-items:center;
  background-size:cover;background-position:center;overflow:hidden;padding:150px 0 90px;}
.hs-hero::before{content:'';position:absolute;inset:0;background:rgba(0,0,0,.44);}
.hs-hero-grid{position:relative;width:100%;display:grid;grid-template-columns:1.15fr .85fr;gap:56px;align-items:center;}
.hs-hero h1{color:#fff;}
.hs-hero .hs-eyebrow{color:var(--hs-yellow);margin-bottom:24px;}
.hs-hero-right{display:flex;flex-direction:column;align-items:flex-start;gap:26px;}
.hs-hero-sub{margin:0;color:#fff;font-weight:600;font-size:clamp(24px,3vw,34px);line-height:1.12;letter-spacing:-.01em;}
.hs-hero-sub em{font-style:normal;color:var(--hs-yellow);}
.hs-hero-right .hs-lead{color:rgba(255,255,255,.92);max-width:460px;}
.hs-hero-note{margin:0;font-size:14px;color:rgba(255,255,255,.75);}

/* ---------- Product cards ---------- */
.hs-cards{max-width:1280px;margin:-140px auto 0;padding:0 40px 96px;
  display:grid;grid-template-columns:1fr 1fr;gap:32px;position:relative;z-index:6;}
.hs-card{display:grid;grid-template-columns:1fr 1fr;gap:36px;border-radius:32px;padding:48px;align-items:stretch;}
.hs-card.amber{background:var(--hs-amber);}
.hs-card.orange{background:var(--hs-orange);}
.hs-card-body{display:flex;flex-direction:column;gap:22px;}
.hs-card-body .eyebrow{margin:0;font-size:16px;color:#000;}
.hs-card-body h3{margin:0;font-weight:400;font-size:clamp(28px,2.6vw,40px);line-height:1.04;letter-spacing:-.015em;color:#000;}
.hs-card-links{margin-top:auto;display:flex;flex-direction:column;gap:14px;align-items:flex-start;}
.hs-card-media{border-radius:24px;overflow:hidden;min-height:260px;background:rgba(0,0,0,.08);}
.hs-card-media img{width:100%;height:100%;object-fit:cover;}

/* ---------- Statement ---------- */
.hs-statement{display:grid;grid-template-columns:1.05fr .95fr;gap:72px;align-items:center;}
.hs-statement-copy{display:flex;flex-direction:column;gap:28px;align-items:flex-start;}
.hs-statement-copy p{margin:0;font-size:clamp(17px,1.6vw,22px);line-height:1.5;max-width:520px;color:var(--hs-g800);}

/* ---------- Stats ---------- */
.hs-stats{display:grid;grid-template-columns:repeat(3,1fr);border-top:1px solid var(--hs-g200);border-bottom:1px solid var(--hs-g200);}
.hs-stat{padding:40px 36px;border-left:1px solid var(--hs-g200);}
.hs-stat:first-child{border-left:none;}
.hs-stat .bar{width:40px;height:5px;background:var(--hs-yellow);margin-bottom:22px;border-radius:2px;}
.hs-stat .n{font-weight:400;font-size:clamp(34px,4vw,54px);line-height:1;letter-spacing:-.02em;}
.hs-stat .l{margin:14px 0 0;font-size:15px;line-height:1.5;color:var(--hs-g600);}

/* ---------- Steps ---------- */
.hs-steps{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;}
.hs-step{background:#fff;border:1px solid var(--hs-g200);border-radius:24px;padding:36px 32px;}
.hs-step .num{display:inline-flex;align-items:center;justify-content:center;width:46px;height:46px;border-radius:50%;background:var(--hs-yellow);color:#000;font-weight:700;font-size:18px;margin-bottom:22px;}
.hs-step h3{margin:0 0 10px;font-weight:600;font-size:23px;line-height:1.2;}
.hs-step p{margin:0;font-size:16px;line-height:1.55;color:var(--hs-g600);}

/* ---------- Coverage ---------- */
.hs-cov{display:grid;grid-template-columns:1fr 1fr;gap:0 64px;}
.hs-cov-row{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:24px 0;border-bottom:1px solid var(--hs-g200);}
.hs-cov-row h4{margin:0;font-weight:600;font-size:21px;}
.hs-cov-row .lim{font-weight:700;font-size:15px;color:var(--hs-g600);white-space:nowrap;}

/* ---------- Safety band ---------- */
.hs-safety{background:#000;color:#fff;}
.hs-safety .hs-eyebrow{color:var(--hs-yellow);}
.hs-safety .hs-h2{color:#fff;}
.hs-safety .hs-sec-head p{color:rgba(255,255,255,.78);}
.hs-safety-grid{display:grid;grid-template-columns:1fr 1fr;gap:28px 48px;}
.hs-safety-item{display:flex;gap:16px;align-items:flex-start;}
.hs-safety-item .tick{flex:none;width:28px;height:28px;border-radius:50%;background:var(--hs-yellow);color:#000;display:grid;place-items:center;font-weight:800;font-size:15px;margin-top:3px;}
.hs-safety-item h3{margin:0 0 5px;font-weight:600;font-size:18px;}
.hs-safety-item p{margin:0;font-size:15px;line-height:1.55;color:rgba(255,255,255,.72);}
.hs-safety-note{margin-top:44px;padding:24px 28px;border-left:4px solid var(--hs-yellow);background:rgba(255,255,255,.06);border-radius:0 16px 16px 0;max-width:720px;font-size:16px;line-height:1.6;}
.hs-safety-note strong{color:var(--hs-yellow);}

/* ---------- Partnership ---------- */
.hs-partner{display:grid;grid-template-columns:1.1fr .9fr;gap:60px;align-items:center;}
.hs-partner-copy p{margin:0 0 16px;font-size:17px;line-height:1.6;color:var(--hs-g800);}
.hs-partner-copy p:last-child{margin-bottom:0;}
.hs-partner-copy a{color:#000;font-weight:700;text-decoration:underline;text-decoration-color:var(--hs-yellow);text-underline-offset:3px;}
.hs-partner-logos{background:#fff;border:1px solid var(--hs-g200);border-radius:24px;padding:48px 40px;display:flex;flex-direction:column;align-items:center;gap:22px;}
.hs-partner-logos img.hs-lg-logo{height:48px;width:auto;}
.hs-partner-logos img.hs-ph-logo{height:38px;width:auto;}
.hs-partner-logos .plus{font-weight:800;font-size:26px;color:var(--hs-orange);}

/* ---------- FAQ ---------- */
.hs-faq-list{max-width:820px;}
.hs-page details{background:#fff;border:1px solid var(--hs-g200);border-radius:16px;margin-bottom:14px;overflow:hidden;}
.hs-page details[open]{border-color:#000;}
.hs-page summary{list-style:none;cursor:pointer;padding:22px 26px;font-weight:700;font-size:18px;display:flex;justify-content:space-between;gap:16px;align-items:center;}
.hs-page summary::-webkit-details-marker{display:none;}
.hs-page summary::after{content:'+';font-size:26px;line-height:1;color:var(--hs-g600);transition:transform var(--hs-dur) var(--hs-ease);}
.hs-page details[open] summary::after{transform:rotate(45deg);}
.hs-page details .ans{padding:0 26px 24px;font-size:16px;line-height:1.6;color:var(--hs-g600);}

/* ---------- Final CTA ---------- */
.hs-final{background:var(--hs-yellow);}
.hs-final .hs-wrap{display:flex;flex-direction:column;align-items:center;text-align:center;gap:24px;}
.hs-final h2{color:#000;}
.hs-final p{margin:0;color:#000;font-size:18px;line-height:1.55;max-width:560px;}

/* ---------- Footer ---------- */
.hs-footer{background:var(--hs-g800);color:var(--hs-g400);padding:48px 0;font-size:14px;}
.hs-foot-inner{display:flex;justify-content:space-between;align-items:center;gap:24px;flex-wrap:wrap;}
.hs-foot-logos{display:flex;align-items:center;gap:16px;background:#fff;padding:12px 18px;border-radius:14px;}
.hs-foot-logos img.hs-lg-logo{height:26px;width:auto;}
.hs-foot-logos img.hs-ph-logo{height:20px;width:auto;}
.hs-foot-logos .x{color:var(--hs-g400);font-weight:600;}
.hs-footer a{color:var(--hs-yellow);text-decoration:none;}
.hs-footer a:hover{text-decoration:underline;}
.hs-disclaim{margin-top:22px;font-size:12.5px;line-height:1.55;color:var(--hs-g600);max-width:920px;}

/* ---------- Responsive ---------- */
@media (max-width:1024px){
  .hs-hero-grid{grid-template-columns:1fr;gap:30px;}
  .hs-hero-right{gap:22px;}
  .hs-card{grid-template-columns:1fr;gap:28px;}
  .hs-card-media{min-height:220px;}
  .hs-statement{grid-template-columns:1fr;gap:36px;}
  .hs-partner{grid-template-columns:1fr;gap:36px;}
  .hs-nav-links{display:none;}
}
@media (max-width:860px){
  .hs-wrap{padding:0 20px;}
  .hs-utility{padding:12px 20px;}
  .hs-navbar{margin-right:14px;padding:14px 18px 14px 20px;gap:16px;}
  .hs-nav-cta .hs-btn{display:none;}
  .hs-cobrand img.hs-lg-logo{height:30px;}
  .hs-cobrand img.hs-ph-logo{height:22px;}
  .hs-hero{min-height:auto;padding:128px 0 72px;}
  .hs-cards{grid-template-columns:1fr;margin-top:-72px;padding:0 20px 72px;}
  .hs-card{padding:32px;}
  .hs-section{padding:64px 0;}
  .hs-sec-head{margin-bottom:36px;}
  .hs-stats{grid-template-columns:1fr;}
  .hs-stat{border-left:none;border-top:1px solid var(--hs-g200);padding:32px 0;}
  .hs-stat:first-child{border-top:none;}
  .hs-steps{grid-template-columns:1fr;}
  .hs-cov{grid-template-columns:1fr;gap:0;}
  .hs-safety-grid{grid-template-columns:1fr;gap:24px;}
  .hs-partner-logos{padding:40px 28px;}
}
@media (prefers-reduced-motion:reduce){
  .hs-page{scroll-behavior:auto;}
  .hs-btn,.hs-arrowlink,.hs-al-ar{transition:none;}
}
.hs-btn:focus-visible,.hs-arrowlink:focus-visible,.hs-page summary:focus-visible,.hs-nav-links a:focus-visible{outline:3px solid var(--hs-yellow);outline-offset:3px;}
`;

const HighstreetLandingPage = () => {
  return (
    <div className="hs-page" data-testid="highstreet-landing-page">
      <Helmet>
        <title>Highstreet + Players Health — Sports Insurance Made Easy</title>
        <meta name="description" content="Building safer sports communities. Sports insurance made easy for the clubs, leagues, camps, and tournaments you run — a free quote, bindable coverage, and an athlete safety platform included at no extra cost." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap" rel="stylesheet" />
        <meta property="og:title" content="Highstreet + Players Health — Sports Insurance Made Easy" />
        <meta property="og:description" content="Building safer sports communities — coverage and an athlete safety platform built for youth and amateur sports." />
        <meta property="og:type" content="website" />
      </Helmet>
      <style>{HS_CSS}</style>

      {/* ---------- HEADER ---------- */}
      <header className="hs-header">
        <div className="hs-utility">
          <a href="https://highstreet.com" target="_blank" rel="noreferrer" data-testid="utility-partners-link">
            Highstreet Partners
            <span style={{ width: 16, height: 16, display: 'inline-flex' }}>
              <svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 17 17 7" /><path d="M7 7h10v10" />
              </svg>
            </span>
          </a>
        </div>
        <div className="hs-navbar">
          <div className="hs-cobrand">
            <img className="hs-lg-logo" src="/highstreet-logo.png" alt="Highstreet Insurance Partners" />
            <span className="x">{'\u00d7'}</span>
            <img className="hs-ph-logo" src="/ph-logo-color.png" alt="Players Health" />
          </div>
          <nav className="hs-nav-links">
            <a href="#coverage">Coverage</a>
            <a href="#safety">Safety</a>
            <a href="#how">How it works</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="hs-nav-cta">
            <QuoteButton size="md" testid="nav-quote-cta">Get my quote</QuoteButton>
          </div>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="hs-hero" style={{ backgroundImage: `url(${HERO_IMG})` }}>
        <div className="hs-wrap hs-hero-grid">
          <div>
            <span className="hs-eyebrow">A Highstreet + Players Health partnership</span>
            <h1 className="hs-display-1">Building safer<br />sports communities.</h1>
          </div>
          <div className="hs-hero-right">
            <p className="hs-hero-sub">Sports insurance made <em>easy</em>.</p>
            <p className="hs-lead">Coverage built for the clubs, leagues, camps, and tournaments you run — with an athlete safety platform included at no extra cost.</p>
            <QuoteButton testid="hero-quote-cta">Get my quote</QuoteButton>
            <p className="hs-hero-note">Takes about 5 minutes. Free, with no obligation to bind.</p>
          </div>
        </div>
      </section>

      {/* ---------- PRODUCT CARDS ---------- */}
      <div className="hs-cards">
        <div className="hs-card amber">
          <div className="hs-card-body">
            <p className="eyebrow">For leagues &amp; clubs:</p>
            <h3>Season-long<br />coverage</h3>
            <div className="hs-card-links">
              <ArrowLink testid="card-league-gl">General liability</ArrowLink>
              <ArrowLink testid="card-league-accident">Participant accident</ArrowLink>
            </div>
          </div>
          <div className="hs-card-media">
            <img src={CARD_IMG_LEAGUE} alt="Youth soccer team playing in a sunlit park" loading="lazy" />
          </div>
        </div>
        <div className="hs-card orange">
          <div className="hs-card-body">
            <p className="eyebrow">For camps &amp; events:</p>
            <h3>Tournaments<br />&amp; camps</h3>
            <div className="hs-card-links">
              <ArrowLink testid="card-event-coverage">Event coverage</ArrowLink>
              <ArrowLink testid="card-event-additional">Additional insured</ArrowLink>
            </div>
          </div>
          <div className="hs-card-media">
            <img src={CARD_IMG_EVENT} alt="Child playing with a ball at golden hour" loading="lazy" />
          </div>
        </div>
      </div>

      {/* ---------- STATEMENT ---------- */}
      <section className="hs-section">
        <div className="hs-wrap hs-statement">
          <h2 className="hs-display-2">Insurance and athlete safety, from people who know sports.</h2>
          <div className="hs-statement-copy">
            <p>Highstreet brings together local agencies across the country. You keep the agent you know; your program gains sports-specialist coverage and a safety platform built by Players Health.</p>
            <AnchorButton href="#coverage" variant="outline" testid="statement-coverage-cta">See what's covered</AnchorButton>
          </div>
        </div>
      </section>

      {/* ---------- STATS ---------- */}
      <section className="hs-section" style={{ paddingTop: 0 }}>
        <div className="hs-wrap">
          <div className="hs-stats">
            <div className="hs-stat">
              <div className="bar" />
              <div className="n">24 hrs</div>
              <p className="l">From application to a bindable quote in your inbox</p>
            </div>
            <div className="hs-stat">
              <div className="bar" />
              <div className="n">$0</div>
              <p className="l">Added cost for the Players Health athlete safety platform</p>
            </div>
            <div className="hs-stat">
              <div className="bar" />
              <div className="n">1 team</div>
              <p className="l">A local agent plus sports-specialist underwriting behind you</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="hs-section" id="how" style={{ background: 'var(--hs-bone)' }}>
        <div className="hs-wrap">
          <div className="hs-sec-head">
            <span className="hs-eyebrow">How it works</span>
            <h2 className="hs-h2">From kickoff to covered in three steps.</h2>
            <p>The questions are written for sports organizations — not generic small businesses — so getting a real quote is quick.</p>
          </div>
          <div className="hs-steps">
            <div className="hs-step">
              <div className="num">1</div>
              <h3>Tell us about your organization</h3>
              <p>Sport, headcount, season dates, and venues. About five minutes, all online.</p>
            </div>
            <div className="hs-step">
              <div className="num">2</div>
              <h3>Get your quote</h3>
              <p>A sports-specialist team reviews your submission and returns a bindable quote, typically within 24 hours.</p>
            </div>
            <div className="hs-step">
              <div className="num">3</div>
              <h3>Bind and play</h3>
              <p>Accept online, pay online, and pull certificates of insurance — including additional insureds for your facilities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- COVERAGE ---------- */}
      <section className="hs-section" id="coverage">
        <div className="hs-wrap">
          <div className="hs-sec-head">
            <span className="hs-eyebrow">The coverage stack</span>
            <h2 className="hs-h2">Built for how youth and amateur sports actually run.</h2>
            <p>One program, placed through Players Health — a licensed managing general agency specializing in youth and amateur athletics.</p>
          </div>
          <div className="hs-cov">
            {COVERAGE.map((c) => (
              <div className="hs-cov-row" key={c.h}>
                <h4>{c.h}</h4>
                <span className="lim">{c.lim}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SAFETY ---------- */}
      <section className="hs-section hs-safety" id="safety">
        <div className="hs-wrap">
          <div className="hs-sec-head">
            <span className="hs-eyebrow">Included with every policy</span>
            <h2 className="hs-h2">The athlete safety platform comes with it. Free.</h2>
            <p>Players Health was founded on a simple idea: the safest environment for an athlete is also the best risk. Every insured organization gets the full safety platform at no added cost.</p>
          </div>
          <div className="hs-safety-grid">
            {SAFETY.map((s) => (
              <div className="hs-safety-item" key={s.h}>
                <span className="tick">{'\u2713'}</span>
                <div><h3>{s.h}</h3><p>{s.p}</p></div>
              </div>
            ))}
          </div>
          <div className="hs-safety-note">
            <strong>Why free?</strong> Safer organizations have fewer and less severe claims. When you invest in safety, your insurance should reward it — that's the whole model.
          </div>
        </div>
      </section>

      {/* ---------- PARTNERSHIP ---------- */}
      <section className="hs-section">
        <div className="hs-wrap hs-partner">
          <div className="hs-partner-copy">
            <div className="hs-sec-head" style={{ marginBottom: 24 }}>
              <span className="hs-eyebrow">Why this partnership</span>
              <h2 className="hs-h2">A local agent. A national safety platform.</h2>
            </div>
            <p>Highstreet is a national network of local insurance agencies — national resources delivered by an agent who lives on your street. Players Health protects youth and amateur sports organizations across the country.</p>
            <p>Together, we're closing the gap between running a great program and protecting one — the right coverage, placed locally, with safety built in.</p>
            <p>Questions before you start? <a href="mailto:quotes@playershealth.com">Talk to a Players Health advisor</a>.</p>
          </div>
          <div className="hs-partner-logos">
            <img className="hs-lg-logo" src="/highstreet-logo.png" alt="Highstreet Insurance Partners" />
            <span className="plus">+</span>
            <img className="hs-ph-logo" src="/ph-logo-color.png" alt="Players Health" />
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="hs-section" id="faq" style={{ background: 'var(--hs-bone)' }}>
        <div className="hs-wrap">
          <div className="hs-sec-head">
            <span className="hs-eyebrow">Good questions</span>
            <h2 className="hs-h2">Before you ask.</h2>
          </div>
          <div className="hs-faq-list">
            {FAQ.map((item, i) => (
              <details key={i} data-testid={`faq-item-${i}`}>
                <summary>{item.q}</summary>
                <div className="ans">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="hs-section hs-final">
        <div className="hs-wrap">
          <h2 className="hs-display-2">Building safer sports<br />communities starts here.</h2>
          <p>Five minutes of questions. A real quote, typically within 24 hours. Coverage and a safety platform built for the way you run sports.</p>
          <QuoteButton variant="inverse" testid="final-quote-cta">Get my quote</QuoteButton>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="hs-footer">
        <div className="hs-wrap">
          <div className="hs-foot-inner">
            <div className="hs-foot-logos">
              <img className="hs-lg-logo" src="/highstreet-logo.png" alt="Highstreet Insurance Partners" />
              <span className="x">{'\u00d7'}</span>
              <img className="hs-ph-logo" src="/ph-logo-color.png" alt="Players Health" />
            </div>
            <div>
              <a href="/highstreet" data-testid="footer-link-highstreet">phquote.com/highstreet</a>
              {'\u00a0\u00b7\u00a0'}
              <a href="mailto:quotes@playershealth.com">quotes@playershealth.com</a>
            </div>
          </div>
          <p className="hs-disclaim">Insurance products are offered through Players Health, a licensed insurance producer. Coverage descriptions on this page are summaries only; actual terms, conditions, limits, and exclusions are governed by the issued policy. Coverage availability may vary by state. Highstreet Insurance Partners is a distribution partner.</p>
        </div>
      </footer>
    </div>
  );
};

export default HighstreetLandingPage;
